export const SITE_URL = 'https://sk-aqua.ru'
export const DEPLOY_BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH ?? ''

export function deployPath(path = '/') {
  if (!path.startsWith('/')) return path
  return `${DEPLOY_BASE_PATH}${path}`
}

export const COMPANY = {
  name: 'ООО «СК-АКВА»',
  shortName: 'СК-АКВА',
  legalName: 'Общество с ограниченной ответственностью «СК-АКВА»',
  phone: '+79170855195',
  phoneDisplay: '+7 (917) 085-51-95',
  email: 'ooo-sk-aqua@yandex.ru',
  foundingDate: '2010',
  ogImage: '/og-image.webp',
  description:
    'Осетровое рыбоводное хозяйство полного цикла. Производство чёрной икры, живой рыбы и мяса осетровых пород.',
  addresses: [
    {
      name: 'Производственный комплекс в Дагестане',
      postalCode: '368808',
      region: 'Республика Дагестан',
      locality: 'с. Краснооктябрьское',
      street: 'ул. Степная, стр. 47',
      display: '368808, Республика Дагестан, Кизлярский район, с. Краснооктябрьское, ул. Степная, стр. 47',
    },
    {
      name: 'Садковая линия в Астраханской области',
      postalCode: '416195',
      region: 'Астраханская область',
      locality: 'с. Новинка',
      street: 'ул. Степная, дом № 6',
      display: '416195, Астраханская область, Володарский район, с. Новинка, ул. Степная, дом № 6',
    },
  ],
}

export const NAV_PAGES = [
  { href: '/products', label: 'Продукция' },
  { href: '/production', label: 'Производство' },
  { href: '/partners', label: 'Партнёрам' },
  { href: '/delivery', label: 'Доставка' },
  { href: '/documents', label: 'Документы' },
  { href: '/contacts', label: 'Контакты' },
]

export const STATIC_SEO_PAGES = [
  { path: '/', priority: 1, changeFrequency: 'weekly' },
  { path: '/products', priority: 0.95, changeFrequency: 'weekly' },
  { path: '/production', priority: 0.8, changeFrequency: 'monthly' },
  { path: '/production/dagestan', priority: 0.75, changeFrequency: 'monthly' },
  { path: '/production/astrakhan', priority: 0.75, changeFrequency: 'monthly' },
  { path: '/delivery', priority: 0.75, changeFrequency: 'monthly' },
  { path: '/documents', priority: 0.7, changeFrequency: 'monthly' },
  { path: '/partners', priority: 0.8, changeFrequency: 'monthly' },
  { path: '/contacts', priority: 0.75, changeFrequency: 'monthly' },
  { path: '/faq', priority: 0.65, changeFrequency: 'monthly' },
]

export function absoluteUrl(path = '/') {
  if (path === SITE_URL) return SITE_URL
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

export function makeMetadata({ title, description, path = '/', images, robots } = {}) {
  const url = absoluteUrl(path)
  const image = images ?? [{ url: COMPANY.ogImage, width: 1536, height: 1024, alt: `${COMPANY.shortName} — осетровое хозяйство` }]

  return {
    title,
    description,
    alternates: { canonical: url },
    robots: robots ?? {
      index: true,
      follow: true,
      googleBot: { index: true, follow: true },
    },
    openGraph: {
      type: 'website',
      url,
      siteName: COMPANY.shortName,
      title,
      description,
      locale: 'ru_RU',
      images: image,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      images: image.map((item) => item.url),
    },
  }
}

export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: COMPANY.name,
    alternateName: COMPANY.shortName,
    url: SITE_URL,
    image: {
      '@type': 'ImageObject',
      url: absoluteUrl(COMPANY.ogImage),
      width: 1536,
      height: 1024,
    },
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl('/icon.png'),
    },
    telephone: COMPANY.phone,
    email: COMPANY.email,
    foundingDate: COMPANY.foundingDate,
    description: COMPANY.description,
    areaServed: { '@type': 'Country', name: 'Россия' },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: COMPANY.phone,
      contactType: 'sales',
      areaServed: 'RU',
      availableLanguage: ['ru'],
    },
    address: COMPANY.addresses.map((address) => ({
      '@type': 'PostalAddress',
      postalCode: address.postalCode,
      addressRegion: address.region,
      addressLocality: address.locality,
      streetAddress: address.street,
      addressCountry: 'RU',
    })),
  }
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: COMPANY.shortName,
    url: SITE_URL,
    publisher: { '@id': `${SITE_URL}/#organization` },
    inLanguage: 'ru-RU',
  }
}

export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.href),
    })),
  }
}

export function faqPageJsonLd(items) {
  if (!items?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }
}

export function localBusinessJsonLd() {
  return {
    ...organizationJsonLd(),
    '@type': 'LocalBusiness',
    priceRange: '₽₽₽',
  }
}
